import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash-es';
import { MethodSettlementService } from '../method-settlement/method-settlement.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

interface BankerEntity {
  LEID: string;
  DIVISION: string;
  MAIN_BUSINESS: string;
  BANKER?: number;
}

@Component({
    selector: 'gtw-gof-settlement',
    templateUrl: './gof-settlement.component.html',
    styleUrls: ['./gof-settlement.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GofSettlementComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {};
  filterParams: any = {};
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;
  rowData: any = {};

  title = "Gof Settlement";
  bankerArray: BankerEntity[] = [];
  loading = true;
  selectedBanker: BankerEntity | null = null;
  crudLoading = false;
  minimise = false;
  isSaveClicked = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private methodSettlementService: MethodSettlementService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData;
    this.appService.SERVER_CONFIG = header.serverConfigData;
    this.GENERAL_CONFIG = header.generalConfigData;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  // Gate function to prevent duplicate initialization
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init() {
    try {
      // Check if locked
      if (this.rowData.Is_Locked_0 && this.rowData.Is_Locked_0 === 1) {
        this._snackBar.open('This LEID / Division combination is locked', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        setTimeout(() => this.cancel(), 30);
        return;
      }

      this.getBankerArray();
    } catch (e) {
      console.error('Error in init:', e);
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  getBankerArray() {
    this.loading = true;

    this.methodSettlementService.getBankerArray(
      this.GENERAL_CONFIG.base_url,
      this.rowData.LEID,
      this.rowData.Division,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.loading = false;

        if (result.callSuccess !== '1') {
          this._snackBar.open(result.errorMessage || 'Error loading data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this.bankerArray = result.jsonObject || [];
          this.selectedBanker = _.find(this.bankerArray, { BANKER: 1 }) || null;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.loading = false;
        console.error('Error loading banker array:', error);
        this._snackBar.open('Error loading data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  onBankerSelect(entity: BankerEntity) {
    this.selectedBanker = entity;
    this.cdr.detectChanges();
  }

  save() {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    if (!this.selectedBanker) {
      this._snackBar.open('Please select a banker', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    this.methodSettlementService.setBanker(
      this.GENERAL_CONFIG.base_url,
      this.selectedBanker.LEID,
      this.selectedBanker.DIVISION,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (result.callSuccess !== '1') {
          this._snackBar.open(result.errorMessage || 'Error saving data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Banker has been set', 'Close', {
            duration: 1000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
